import axios from 'axios';
import { camelCase } from 'lodash';
import Papa from 'papaparse';
import {
  AssetsData,
  cleanObject,
  cleanObjectsInArray,
  NerileaData,
  transform,
} from './dataHelpers';
import {
  BlueprintRaw,
  MerchantLevel,
  IWorker,
  IWorkerLevel,
} from './dataSlice';

// Kabam Blueprints
const kabamBlueprintUrl =
  'https://docs.google.com/spreadsheets/d/1WLa7X8h3O0-aGKxeAlCL7bnN8-FhGd3t7pz2RCzSg8c/pub?gid=1558235212&output=csv';

// kabam Merchant Levels
const kabamMerchantLevelUrl =
  'https://docs.google.com/spreadsheets/d/1WLa7X8h3O0-aGKxeAlCL7bnN8-FhGd3t7pz2RCzSg8c/pub?gid=1602631852&output=csv';

// kabam Collection Book
// const kabamCollectionBookUrl =
//   'https://docs.google.com/spreadsheets/d/1WLa7X8h3O0-aGKxeAlCL7bnN8-FhGd3t7pz2RCzSg8c/pub?gid=805699567&output=csv';

// Nerilea
const nerileaUrl =
  'https://docs.google.com/spreadsheets/d/e/2PACX-1vTLObUYiJbFeuWeGuhAIwXFDjDPiAPIqsvAwem6nKqtZ0BKRsrfRtW6uCPK4eBzpoffG7ciY9GvNHQs/pub?output=csv';

const affinityMapUrl =
  'https://docs.google.com/spreadsheets/d/1T-cfT73u0zweSsr4Nhbwzz7Ymkc_r8ArypAGh8M6d8c/pub?gid=173917257&output=csv';

// Fan Kit Assets
const assetsUrl =
  'https://docs.google.com/spreadsheets/d/1T-cfT73u0zweSsr4Nhbwzz7Ymkc_r8ArypAGh8M6d8c/pub?gid=142395081&output=csv';

// Workers
const workersUrl =
  'https://docs.google.com/spreadsheets/d/1WLa7X8h3O0-aGKxeAlCL7bnN8-FhGd3t7pz2RCzSg8c/pub?gid=1935922361&output=csv';

// worker levels
const workerLevelsUrl =
  'https://docs.google.com/spreadsheets/d/1WLa7X8h3O0-aGKxeAlCL7bnN8-FhGd3t7pz2RCzSg8c/pub?gid=370031681&output=csv';

// Auto extract headers using this URL and then copy paste into the code above, then add transformHeader to clean up the headers as needed
// https://sheets.googleapis.com/v4/spreadsheets/1WLa7X8h3O0-aGKxeAlCL7bnN8-FhGd3t7pz2RCzSg8c/values/Blueprints!1:1?valueRenderOption=FORMULA&key=AIzaSyD-cQxhfvCtWOp7aQ22uGeCS4UaWDd8uag

export const kabamBlueprintPromise = () =>
  new Promise<BlueprintRaw[]>((resolve) => {
    const duplicateHeaders: { [key: string]: number } = {
      requiredWorker: 1,
      workerLevel: 1,
      component: 1,
      componentQuality: 1,
      amountNeeded: 1,
      craftsNeeded: 1,
      starforgedCraftsNeeded: 1,
      shardsNeeded: 1,
    };

    let encounteredStarforgedMilestone = false;

    Papa.parse<BlueprintRaw>(kabamBlueprintUrl, {
      download: true,
      dynamicTyping: true,
      complete: (results) => {
        const blueprints = results.data.map((bp) => {
          // Search craftingUpdates1-5 for "x1.25 Value Increase"
          for (let i = 1; i <= 5; i++) {
            const craftingUpdateKey =
              `craftingUpgrade${i}` as keyof BlueprintRaw;
            const craftingUpdateValue = bp[craftingUpdateKey];
            if (
              typeof craftingUpdateValue === 'string' &&
              craftingUpdateValue.includes('Value Increase')
            ) {
              const match = craftingUpdateValue.match(/x([\d.]+)/);
              if (match) {
                bp.valueMilestoneMultiplier = parseFloat(match[1]);
              }
            }
          }

          // Search starforgedMilestone1-5 for "x1.25 Value Increase"
          for (let i = 1; i <= 5; i++) {
            const starforgedMilestoneKey =
              `starforgedMilestone${i}` as keyof BlueprintRaw;
            const starforgedMilestoneValue = bp[starforgedMilestoneKey];
            if (
              typeof starforgedMilestoneValue === 'string' &&
              starforgedMilestoneValue.includes('Value Increase')
            ) {
              const match = starforgedMilestoneValue.match(/x([\d.]+)/);
              if (match) {
                bp.valueStarforgedMultiplier = parseFloat(match[1]);
              }
            }
          }

          // Handle type adjustments
          if (bp.type === 'Enchantment') {
            if (bp.name.toLowerCase().includes('spirit')) {
              bp.type = 'Spirit';
            }
            if (bp.name.toLowerCase().includes('element')) {
              bp.type = 'Element';
            }
          }
          return cleanObject(bp);
        });
        resolve(blueprints);
      },
      header: true,
      transformHeader: (header, hIndex) => {
        let camelHeader = camelCase(header);

        if (camelHeader === 'starforgedMilestone1') {
          encounteredStarforgedMilestone = true;
          duplicateHeaders['craftsNeeded'] = 1; // Reset craftsNeeded counter
        }

        if (encounteredStarforgedMilestone && camelHeader === 'craftsNeeded') {
          camelHeader = 'starforgedCraftsNeeded';
        }

        if (duplicateHeaders[camelHeader]) {
          duplicateHeaders[camelHeader]++;
          camelHeader = `${camelHeader}${duplicateHeaders[camelHeader] - 1}`;
        }
        switch (camelHeader) {
          case 'elementalAffinity':
            return 'elementAffinity';
        }
        switch (hIndex) {
          case 24:
            return 'iron';
          case 25:
            return 'wood';
          case 26:
            return 'leather';
          case 27:
            return 'herbs';
          case 28:
            return 'steel';
          case 29:
            return 'ironwood';
          case 30:
            return 'fabric';
          case 31:
            return 'oils';
          case 32:
            return 'mana';
          case 33:
            return 'gems';
          case 34:
            return 'essence';
          case 35:
            return 'stardust';
          default:
            return camelHeader;
        }
      },
      transform,
    });
  });

export const kabamMerchantLevelPromise = () =>
  new Promise<MerchantLevel[]>((resolve) => {
    Papa.parse<MerchantLevel>(kabamMerchantLevelUrl, {
      download: true,
      dynamicTyping: true,
      header: true,
      transformHeader: (header, hIndex) => {
        const camelHeader = camelCase(header);
        switch (camelHeader) {
          case 'merchantLevel':
            return 'level';
          case 'marketItemTierUnlock':
            return 'tierUnlock';
          default:
            return camelHeader;
        }
      },
      complete: (results) => {
        resolve(cleanObjectsInArray(results.data));
      },
      transform,
    });
  });

export const affinityMapPromise = () =>
  new Promise<{ [key: string]: string }>((resolve) => {
    const affinityMap: { [key: string]: string } = {};
    Papa.parse<{ blueprint: string; affinity: string }>(affinityMapUrl, {
      download: true,
      dynamicTyping: true,
      header: true,
      transformHeader: (header, hIndex) => {
        return camelCase(header);
      },
      complete: (results) => {
        results.data.forEach((row) => {
          affinityMap[row.blueprint] = row.affinity;
        });
        resolve(affinityMap);
      },
    });
  });

export const nerileaPromise = () =>
  new Promise<NerileaData[]>((resolve) => {
    Papa.parse<NerileaData>(nerileaUrl, {
      download: true,
      dynamicTyping: true,
      complete: (results) => {
        resolve(cleanObjectsInArray(results.data));
      },
      header: true,
      transformHeader: (header, hIndex) => {
        const camelHeader = camelCase(header);
        switch (camelHeader) {
          case 'elementalAffinity':
            return 'elementAffinity';
          case 'attack':
            return 'atk';
          case 'defense':
            return 'def';
          case 'health':
            return 'hp';
          case 'evasion':
            return 'eva';
          case 'criticalHitChance':
            return 'crit';
          case 'builtInElement':
            return 'builtInElementNerilea';
          case 'builtInSpirit':
            return 'builtInSpiritNerilea';
          case 'valueMilestoneMultiplier':
            return 'valueMilestoneMultiplierNerilea';
        }
        return camelHeader;
      },
      transform,
    });
  });

export const assetsPromise = () =>
  new Promise<AssetsData[]>((resolve) => {
    Papa.parse<AssetsData>(assetsUrl, {
      download: true,
      dynamicTyping: true,
      complete: (results) => {
        resolve(results.data);
      },
      header: true,
      transformHeader: (header, hIndex) => {
        return camelCase(header);
      },
    });
  });

export const currentTimePromise = () =>
  new Promise<number>((resolve, reject) => {
    axios
      .get('https://worldtimeapi.org/api/timezone/Europe/Copenhagen')
      .then((response) => {
        resolve(response.data.unixtime);
      })
      .catch((error) => {
        reject(error);
      });
  });

export const workersPromise = () =>
  new Promise<IWorker[]>((resolve) => {
    Papa.parse<IWorker>(workersUrl, {
      download: true,
      dynamicTyping: true,
      complete: (results) => {
        resolve(cleanObjectsInArray(results.data));
      },
      header: true,
      transformHeader: (header, hIndex) => {
        return camelCase(header);
      },
      transform,
    });
  });

export const workerLevelsPromise = () =>
  new Promise<IWorkerLevel[]>((resolve) => {
    Papa.parse<IWorkerLevel>(workerLevelsUrl, {
      download: true,
      dynamicTyping: true,
      complete: (results) => {
        resolve(cleanObjectsInArray(results.data));
      },
      header: true,
      transformHeader: (header, hIndex) => {
        const camelHeader = camelCase(header);
        switch (camelHeader) {
          case 'workerLevel':
            return 'level';
          case 'craftingSpeedBonus':
            return 'speedBonus';
          case 'xpNeeded':
            return 'xp';
          default:
            return camelHeader;
        }
      },
      transform: (value, header) => {
        const newValue = transform(value, header);
        if (newValue && header === 'speedBonus') {
          return Number(newValue.replace('%', ''));
        }
        return newValue;
      },
    });
  });
